import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert, Checkbox, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import { WarningAmberOutlined } from '@mui/icons-material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { ISurveyProductDataPreviewRow } from '../../../shared/services/survey/survey.model';

//Review-then-apply for supplier product data: each row shows the supplier's proposed figures
//next to the product's current factor. Nothing changes until rows are selected and applied.
//Products whose current factor comes from a supplier EPD need their own confirmation tick.

interface Props {
  open: boolean;
  onClose: () => void;
  surveyId: string;
  successHandler: () => void;
}

const numberOrDash = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : value.toLocaleString(undefined, { maximumFractionDigits: 4 });

export const SurveyProductDataReviewDialog = ({ open, onClose, surveyId, successHandler }: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [rows, setRows] = useState<ISurveyProductDataPreviewRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [epdConfirmed, setEpdConfirmed] = useState<Set<string>>(new Set());
  const [refusedEpdNames, setRefusedEpdNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const preview = await surveyAdminService.getProductDataPreview(surveyId);
      setRows(preview.rows);
      setSelected(new Set(preview.rows.filter(row => !row.appliedAt).map(row => row.productResponseId)));
      setEpdConfirmed(new Set());
      setRefusedEpdNames([]);
    } catch {
      setRows([]);
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error loading the responses, please try again', backgroundColor: 'text.primary' });
    } finally {
      setIsLoading(false);
    }
  }, [surveyId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggleSelected = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleEpdConfirmed = (goodId: string) => {
    setEpdConfirmed(prev => {
      const next = new Set(prev);
      if (next.has(goodId)) next.delete(goodId);
      else next.add(goodId);
      return next;
    });
  };

  const selectedRows = rows.filter(row => selected.has(row.productResponseId));
  const unconfirmedEpd = selectedRows.filter(row => row.epdOverwriteHazard && !epdConfirmed.has(row.capitalGoodId));
  const applyDisabled = selectedRows.length === 0 || unconfirmedEpd.length > 0;

  const handleApply = async () => {
    setIsSubmitting(true);
    try {
      const result = await surveyAdminService.applyProductData(
        surveyId,
        selectedRows.map(row => row.productResponseId),
        [...epdConfirmed],
      );
      userNotificationsContext.openSnackbar({
        open: true,
        message: `Applied ${result.applied} product figure${result.applied === 1 ? '' : 's'}`,
        backgroundColor: 'primary.dark',
      });
      // The backend refuses EPD-protected goods that were not explicitly confirmed. Keep
      // the dialog open and name them so they are acted on rather than silently kept.
      const refused = result.refusedEpdGoods ?? [];
      if (refused.length > 0) {
        await load();
        setRefusedEpdNames(refused.map(good => good.name));
        return;
      }
      successHandler();
      onClose();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'An error occurred, please try again', backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title='Review supplier product data'
      cancelHandler={onClose}
      submitHandler={handleApply}
      isSubmitting={isSubmitting || isLoading}
      disableSaveButton={applyDisabled}
      submitButtonText={`Apply ${selectedRows.length || ''} selected`.trim()}
      maxWidth='lg'
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          Applying updates each product's emission factor and marks it verified by the supplier.
          Existing emissions keep the factor they were calculated with; only future entries use
          the new figure.
        </Typography>
        {refusedEpdNames.length > 0 && (
          <Alert severity='warning'>
            These products kept their existing EPD factor because replacement was not confirmed:{' '}
            {refusedEpdNames.join(', ')}. Tick Replaces EPD factor on each row to apply them.
          </Alert>
        )}
        {!isLoading && rows.length === 0 && (
          <Typography color='text.secondary' sx={{ py: 2 }}>
            No submitted product data is waiting for review.
          </Typography>
        )}
        {rows.length > 0 && (
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'></TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>Current kg CO2e / unit</TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>Proposed kg CO2e / unit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Proposed unit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.productResponseId}>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        checked={selected.has(row.productResponseId)}
                        disabled={!!row.appliedAt}
                        onChange={() => toggleSelected(row.productResponseId)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{row.goodName}</Typography>
                      {row.modelNumber && (
                        <Typography variant='caption' color='text.secondary'>{row.modelNumber}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.supplier?.name ?? '—'}</TableCell>
                    <TableCell align='right'>
                      {numberOrDash(row.current.kgCarbonEmission)}
                      {row.current.unitName ? ` per ${row.current.unitName}` : ''}
                    </TableCell>
                    <TableCell align='right'>{numberOrDash(row.proposed.kgCarbonEmission)}</TableCell>
                    <TableCell>
                      {row.proposed.unitName ?? '—'}
                      {row.proposed.unitAmount !== null && row.proposed.unitAmount !== undefined
                        ? ` (x ${numberOrDash(row.proposed.unitAmount)})`
                        : ''}
                    </TableCell>
                    <TableCell>
                      {row.appliedAt ? (
                        <Chip size='small' label='Applied' variant='outlined' color='success' />
                      ) : row.epdOverwriteHazard ? (
                        <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                          <Tooltip title='The current factor comes from a supplier EPD. Tick to confirm replacing the EPD figure with this response.' arrow>
                            <Chip
                              size='small'
                              icon={<WarningAmberOutlined />}
                              label='Replaces EPD factor'
                              variant='outlined'
                              color='warning'
                            />
                          </Tooltip>
                          <Checkbox
                            size='small'
                            checked={epdConfirmed.has(row.capitalGoodId)}
                            onChange={() => toggleEpdConfirmed(row.capitalGoodId)}
                          />
                        </Stack>
                      ) : (
                        <Chip size='small' label='Awaiting review' variant='outlined' />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </DialogWrapper>
  );
};
